/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { memo } from 'react';

import { VisuallyHidden, typography2 } from '@coursera/cds-core';

export type ChipButtonProps = {
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
  children?: React.ReactNode;
  isActive?: boolean;
  isDisabled?: boolean;
  showLabel?: boolean;
  count?: number;
  showCount?: boolean;
};

const styles = {
  chipButton: css`
    ${typography2.actionSecondary}
    align-items: center;
    display: flex;
    border-radius: 20px;
    padding: var(--cds-spacing-100) var(--cds-spacing-200);
    margin: 0 3px;
    height: 36px;
    gap: 4px;
    font-size: 14px;
    border: 1px solid var(--cds-color-grey-100);
    background-color: var(--cds-color-neutral-background-primary);
    outline-offset: 3px;
    outline-width: 1px;

    &:hover {
      background-color: var(--cds-color-neutral-background-primary-weak);
    }

    &:active {
      background-color: var(--cds-color-neutral-background-primary-weak);
      border-color: var(--cds-color-grey-100);
    }

    .prefix,
    .suffix {
      height: var(--cds-spacing-200);

      svg {
        height: var(--cds-spacing-200);
        width: var(--cds-spacing-200);
      }
    }

    &:disabled {
      background-color: var(--cds-color-neutral-background-primary);
      border: 1px solid var(--cds-color-neutral-disabled-strong);
      color: var(--cds-color-neutral-disabled-strong);

      &:hover,
      &:focus {
        background-color: var(--cds-color-neutral-background-primary);
        border: 1px solid var(--cds-color-neutral-disabled-strong);
        color: var(--cds-color-neutral-disabled-strong);
      }
    }
  `,

  circleButton: css`
    .prefix {
      margin-left: -5px;
      margin-right: -5px;
    }
  `,

  isSelected: css`
    background: var(--cds-color-neutral-background-primary-weak);
    border: 1px solid var(--cds-color-neutral-stroke-primary-weak);

    &:hover,
    &:focus {
      background: var(--cds-color-neutral-background-primary-weak);
    }

    &:disabled {
      background-color: var(--cds-color-neutral-disabled-strong);
      border: 1px solid var(--cds-color-neutral-disabled-strong);
      color: var(--cds-color-neutral-primary-invert);

      &:hover,
      &:focus {
        background-color: var(--cds-color-neutral-disabled-strong);
        border: 1px solid var(--cds-color-neutral-disabled-strong);
        color: var(--cds-color-neutral-primary-invert);
      }
    }
  `,
  chipLabel: css`
    white-space: nowrap;
  `,
};

export enum CHIP_BUTTON_STATES {
  ACTIVE = 'ACTIVE',
  DISABLED = 'DISABLED',
  DISABLED_ACTIVE = 'DISABLED_ACTIVE',
  INACTIVE = 'INACTIVE',
}

const CHIP_BUTTON_STATE_CONFIGS = {
  [`${CHIP_BUTTON_STATES.ACTIVE}`]: {
    testId: 'chip-button-active',
  },
  [`${CHIP_BUTTON_STATES.DISABLED}`]: {
    testId: 'chip-button-disabled',
  },
  [`${CHIP_BUTTON_STATES.DISABLED_ACTIVE}`]: {
    testId: 'chip-button-disabled-active',
  },
  [`${CHIP_BUTTON_STATES.INACTIVE}`]: {
    testId: 'chip-button-inactive',
  },
} as const;

export const getInitialState = (isActive?: boolean, isDisabled?: boolean) => {
  if (isActive && !isDisabled) {
    return CHIP_BUTTON_STATES.ACTIVE;
  } else if (!isActive && isDisabled) {
    return CHIP_BUTTON_STATES.DISABLED;
  } else if (isActive && isDisabled) {
    return CHIP_BUTTON_STATES.DISABLED_ACTIVE;
  } else {
    return CHIP_BUTTON_STATES.INACTIVE;
  }
};

export const ChipButton = memo(
  ({
    onClick,
    prefix,
    isActive,
    isDisabled,
    suffix,
    showLabel = true,
    showCount = true,
    count,
    children,
  }: ChipButtonProps) => {
    const chipButtonState = getInitialState(isActive, isDisabled);
    const wrappedChildren = children ? <div css={styles.chipLabel}>{children}</div> : null;

    return (
      <button
        onClick={onClick}
        css={[styles.chipButton, isActive && styles.isSelected, !showLabel && !showCount && styles.circleButton]}
        type="button"
        data-testid={CHIP_BUTTON_STATE_CONFIGS[chipButtonState].testId}
        disabled={isDisabled}
      >
        {prefix && <div className="prefix">{prefix}</div>}
        {wrappedChildren && (showLabel ? wrappedChildren : <VisuallyHidden>{wrappedChildren}</VisuallyHidden>)}
        {count !== undefined && showCount && <span>({count})</span>}
        {suffix && <div className="suffix">{suffix}</div>}
      </button>
    );
  }
);

export default ChipButton;
